/*
XOL-67 (B4, v2.1 V1): the words of an RTA command's REFUSAL, keyed on the API's `code` (pure: no Vue, no store, no
axios). Every refusal of the RTA routes is { code, message, error } (API DEV-7, WorkflowException) plus, on a
409, the rows read under the lock; the SPA owns the copy and keys it on `code`, never on `message`.

  parseFailure(err)         { status, code, data, errors } from an axios error (all null for a network error)
  refusal(code, ctx)        { title, message, duration, type, reread } for a code, or null for none
  isReplay(data, command, stage)  is a 200 `duplicate` a replay to accept (Propose → now Client Review, Back to Searching → now Searching; P-S19)
  reasonRequired(command)   the inline copy of 422 reason_required, by the command that was refused
  saveRefusal(err)          what KIND of refusal a versioned field save (request or option, D159) got, in the server's order: locks before conflicts
  staleConflicts(data)      a 409 stale_field's `conflicts` as [{ path, field, current, changedAtVersion, by, org, at }]
  staleNotice(data, ctx)    the existing "Your change wasn't saved" card for those conflicts, in requestSyncCopy's strings
  failureNotice(err, ctx)   refusal(...) of an axios error, with the fallbacks (network, unknown code)

The copy is UX spec v3 section 8 (errors by code) over the revised spec section 7, and the v2 UX spec section 4.6 (Refusals) for the
codes v2 adds: nothing_to_propose, option_approved, already_approved, wrong_phase, not_approved (API section 3.2). v2.1 (delta API-40, D159)
drops first_option and location_locked and adds request_locked {fields} (the request, from the first proposal on: decision 157) and
version_required; option_locked also answers a field save. v2 dropped nominal_locked, stale_nominal and use_transition with the endpoints behind
them; the request and the options now conflict the way every versioned request does (409 stale_field, one card for all of them).
"Client Response" is the Review section now, so the copy says "review". A `duration` of 0 stays on screen until it is closed (the Tower Co must read it), 5500
otherwise. `reread` says the RTA, the layer and the list are read again after the toast.

ctx { telco, towerCo, role ('client' | 'tcc' | 'tce'), optionNo, changedOptionNo, changedByParty, command, extras }
  command  the refused command, where one code reads differently by command: 'request' / 'option' (a versioned field save, D159) for decision_locked
           and not_allowed; 'decline' (Step 4 Decline) / 'reject' (Reject RTA…) /
           'submit' / 'respond' (the Review section) for reason_required; 'delete': every refusal of a delete has the title
           "Not deleted"; option_locked names the option's status, decision_locked says the option wasn't deleted (decision 121) and
           wrong_phase says options are only deleted in Searching, a review needs Client Review, a 'decision' (the Step 4 auto-save, P-S11) needs Client Approved
  changedByParty  'towerco' when the unsubmitted change on the approved option is the Tower Co's own (the summary's approvedChangedByParty):
           response_changed then names no one to wait for
  extras   the refusal's own keys beside { code, message, error } (API DEV-59 and section 3.2: `optionStatus` of option_locked and
           not_approved, `optionNo` of option_approved and already_approved, `stage` of wrong_phase ...). failureNotice fills it from
           the response; the option number and the stage are read from it when the caller did not pass them

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import { copyText, fieldLabel } from '@/lib/requestSyncCopy'
import { nominalFieldOf, NOMINAL_PATH_PREFIX } from '@/lib/rtaNominal'

export var TITLE = 'Not saved'

// The title of a refused option delete (decision 121, Carlos): UX v3 section 8 says "Not saved" for every refusal, but a delete
// saves nothing and the option is still there, so a refused delete (ctx.command 'delete') says "Not deleted".
export var TITLE_NOT_DELETED = 'Not deleted'

export var MSG_NETWORK = 'Unable to reach the server. Your changes may not have been saved — check your connection and try again.'

function present (v) {
  return v !== undefined && v !== null && v !== ''
}

/**
 * What an axios error says: { status, code, data, errors }. A network error (no response) is all null.
 * `code` is read only when it is a string (a 422 without one is Laravel validation).
 */
export function parseFailure (err) {
  var response = err && err.response

  if (!response) {
    return { status: null, code: null, data: null, errors: null }
  }

  var data = response.data && typeof response.data === 'object' ? response.data : null

  return {
    status: response.status || null,
    code: data && typeof data.code === 'string' ? data.code : null,
    data: data,
    errors: data && data.errors && typeof data.errors === 'object' ? data.errors : null
  }
}

function option (n) {
  return present(n) && n !== 0 ? 'option ' + n : 'an option'
}


/**
 * The inline copy of 422 reason_required (UX spec section 4.6 and 6): "Enter a reason to decline this option." at the Step 4 Decline,
 * "Enter a reason to reject this RTA." at Reject RTA…, and "Enter remarks to decline this option." at Submit review (a Declined
 * review needs remarks) and at an auto-save-time check.
 */
export function reasonRequired (command) {
  switch (command) {
    case 'decline':
      return 'Enter a reason to decline this option.'
    case 'reject':
      return 'Enter a reason to reject this RTA.'
    default:
      return 'Enter remarks to decline this option.'
  }
}

// v2.2 (decision 173): the refused paths of a request_locked, when they are Site Name and / or Site ID and nothing else: { label, both }, else null.
// A path is the v3 one ('state.nominal.siteName'), the create one ('nominal.siteName') or the bare field.
function namesRefused (fields) {
  if (!Array.isArray(fields) || fields.length === 0) {
    return null
  }

  var name = false
  var id = false

  for (var i = 0; i < fields.length; i++) {
    var last = String(fields[i]).replace(/^(state\.)?nominal\./, '')

    if (last === 'siteName') {
      name = true
    } else if (last === 'siteId') {
      id = true
    } else {
      return null
    }
  }

  return { label: name && id ? 'Site Name and Site ID' : (name ? 'Site Name' : 'Site ID'), both: name && id }
}

// The option number a refusal names: the caller's, else the one the API put in the refusal's extras (`optionNo`).
function optionNoOf (ctx) {
  if (present(ctx.optionNo) && ctx.optionNo !== 0) {
    return ctx.optionNo
  }

  return ctx.extras && present(ctx.extras.optionNo) && Number(ctx.extras.optionNo) !== 0 ? ctx.extras.optionNo : null
}

// The state each command asks for: a replay is the answer once that state already holds.
var REPLAY_STAGE = { propose: 'Client Review', backToDraft: 'Searching' }

/**
 * Is this 200 answer of Propose / Back to Searching a REPLAY to treat as success (plan-v2 P-S19)? The API sends `duplicate: true` only once the
 * requested state holds and only to the side that asked; in every other case the real refusal answers (option_approved, decision_locked,
 * request_closed, wrong_phase ...). So it is keyed per command: Propose is a replay when the RTA is now in Client Review, Back to Searching
 * when it is now in Searching. `stage` is the SERVER's stage (the answer's action, or what the caller read after it); without one the answer's own
 * `action.status` is used, and with neither the flag alone is taken (the API only sets it once the state holds).
 *
 * @param {Object} data    the 200 body
 * @param {String} command 'propose' | 'backToDraft' (any other command has no replay)
 * @param {String} [stage] the RTA's stage after the answer
 */
export function isReplay (data, command, stage) {
  if (!data || typeof data !== 'object' || data.duplicate !== true || !Object.prototype.hasOwnProperty.call(REPLAY_STAGE, command)) {
    return false
  }

  var at = present(stage) ? stage : (data.action && present(data.action.status) ? data.action.status : null)

  return at === null || at === REPLAY_STAGE[command]
}

/**
 * The toast of a refusal code: { code, title, message, duration, type, reread }, or null for a code with no toast.
 */
export function refusal (code, ctx) {
  ctx = ctx || {}

  var telco = ctx.telco || 'the client'
  var tc = ctx.towerCo || 'the Tower Co'
  var role = ctx.role || 'client'

  function out (message, duration, reread) {
    return {
      code: code,
      title: ctx.command === 'delete' ? TITLE_NOT_DELETED : TITLE,
      message: message,
      duration: duration === undefined ? 5500 : duration,
      type: 'warning',
      reread: reread !== false
    }
  }

  switch (code) {
    case 'invalid_state':
      return out('This RTA changed while you had it open. Its latest status is now shown.')

    case 'request_closed':
      return out('This RTA is already closed, so nothing can change. Its final status is now shown.')

    case 'stale_decision':
      return out('The ' + tc + ' decision changed while you had it open. A change by ' + telco + ' may have undone it. The latest decision is now shown.', 0)

    case 'decision_locked':
      // Propose and Back to Searching in Finalize (P-S19: the real refusal answers there, never a replay): the options can't change.
      if (ctx.command === 'propose' || ctx.command === 'backToDraft') {
        return out(role === 'client'
          ? tc + ' has locked its decision, so the options can\'t change. Ask ' + tc + ' to unlock it if this needs to change.'
          : (role === 'tcc'
            ? 'The ' + tc + ' decision is locked, so the options can\'t change. Unlock it first if they still need to change.'
            : 'The ' + tc + ' decision is locked, so the options can\'t change. Ask a project, finance or admin manager to unlock it if they still need to change.'), 0)
      }

      // A request save (P-S27: Finalize answers decision_locked). An OPTION save in Finalize is option_locked instead (P-S28), so there is no arm for it.
      if (ctx.command === 'request') {
        return out(role === 'client'
          ? tc + ' has locked its decision, so the request can\'t change. Ask ' + tc + ' to unlock it if this needs to change.'
          : 'The ' + tc + ' decision is locked, so the request can\'t change. Unlock it first if it still needs to change.', 0)
      }

      // A refused delete says what was not done (the other copy is about a response or a save).
      if (ctx.command === 'delete') {
        return out(role === 'client'
          ? tc + ' has locked its decision, so the option wasn\'t deleted. Ask ' + tc + ' to unlock it if this needs to change.'
          : (role === 'tcc'
            ? 'The ' + tc + ' decision is locked, so the option wasn\'t deleted. Unlock it first if options still need to change.'
            : 'The ' + tc + ' decision is locked, so the option wasn\'t deleted. Ask a project, finance or admin manager to unlock it if options still need to change.'), 0)
      }

      if (role === 'client') {
        return out(tc + ' has locked its decision, so your review wasn\'t saved. Ask ' + tc + ' to unlock it if this needs to change.', 0)
      }

      return out(role === 'tcc'
        ? 'The ' + tc + ' decision is locked, so this wasn\'t saved. Unlock it first if options still need to change.'
        : 'The ' + tc + ' decision is locked, so this wasn\'t saved. Ask a project, finance or admin manager to unlock it if options still need to change.', 0)

    case 'client_not_approved':
      return out(telco + ' no longer approves an option, so the RTA can\'t be approved. The latest reviews are now shown.')

    case 'option_locked':
      // A client deleting an option it may not delete (API DEV-57): 409 option_locked with the option's status, not 403. This is the
      // race fallback: the button is disabled for the client's own options that have moved on (decision 122), so it shows only when the
      // option moved while the page was open. `optionStatus` is read as sent; an answer without it says the same without the status.
      // The RTA, the layer and the list are read again, so the row the user sees is the latest.
      if (ctx.command === 'delete') {
        var status = ctx.extras && present(ctx.extras.optionStatus) ? String(ctx.extras.optionStatus) : ''
        var which = present(ctx.optionNo) && ctx.optionNo !== 0 ? 'Option ' + ctx.optionNo : 'This option'

        return out(status
          ? which + ' is ' + status + ' now, so it can\'t be deleted. Its latest status is now shown.'
          : which + ' can\'t be deleted any more. Its latest status is now shown.')
      }

      return out((present(ctx.optionNo) ? 'Option ' + ctx.optionNo : 'This option') + ' can\'t be changed any more. Its latest status is now shown.')

    case 'option_limit':
      return out('This RTA already has 5 live options. Delete or decline one to add another.')

    case 'nothing_to_propose':
      return out('There\'s no Draft option to propose. Add an option first.')

    case 'option_approved':
      // API-3, API-4, API-5: an approved option stands, so the options can't change (propose, back to draft, add).
      return out((optionNoOf(ctx) ? 'Option ' + optionNoOf(ctx) : 'An option') + ' is approved, so the options can\'t change now. Change its review first.')

    case 'already_approved':
      // API-7 (I-10): a second Approved. The Review Status select already disables it; this is the race fallback.
      return out(optionNoOf(ctx)
        ? 'Option ' + optionNoOf(ctx) + ' is already approved. Only one option can be approved: change option ' + optionNoOf(ctx) + '\'s review first.'
        : 'An option is already approved. Only one option can be approved: change its review first.')

    case 'wrong_phase':
      // API-7 (a review in Searching), API-18 (a delete outside Searching). `stage` is the RTA's, read from the refusal.
      if (ctx.command === 'delete') {
        return out('Options can only be deleted in Searching. Press Back to Searching first.')
      }

      var stage = ctx.extras && present(ctx.extras.stage) ? String(ctx.extras.stage) : 'Searching'

      // P-S19: the other side proposed first, so this Propose is not a replay (a replay goes to the side that asked) and is refused.
      if (ctx.command === 'propose') {
        return out('The other side proposed first. The RTA has been read again.')
      }

      // P-S11: a decision outside Client Approved (a late auto-save after a reversal or a Decline, a stale Step 4).
      if (ctx.command === 'decision') {
        return out('This RTA is in ' + stage + ', so the decision wasn\'t saved. Its latest status is now shown.')
      }

      return out(ctx.command === 'submit' || ctx.command === 'respond'
        ? 'This RTA is in ' + stage + '. Reviews open when the options are proposed.'
        : 'This RTA is in ' + stage + ', so that can\'t be done now. Its latest status is now shown.')

    case 'not_approved':
      // API-9: the Step 4 Decline of an option that is no longer the approved one (a review reversed it meanwhile).
      return out((optionNoOf(ctx) ? 'Option ' + optionNoOf(ctx) : 'The option') + ' isn\'t approved any more, so it wasn\'t declined. The RTA has been read again.')

    case 'request_locked':
      // Decisions 157, 173 (D159): 409 request_locked {fields}, UX spec 4.6 (a request save). From the first proposal on every request field, Site Name and
      // Site ID included, is a Tower Co manager's; a value sent unchanged passes. The SPA hides the control from everyone else, so this is the fallback for
      // someone who proposed while the viewer was typing, or a stale page. When the refused fields are the names ALONE (v2.2: a Telco fixing a typo in
      // them) the line names them; any other path in `fields` is the general line. It is a LOCK: the server answers it before a stale_field, and so does this table.
      var names = namesRefused(ctx.extras && ctx.extras.fields)
      var manager = ' manager can change '

      if (names) {
        return out('The ' + names.label + (names.both ? ' were' : ' was') + ' not saved: options have been proposed, so only a ' + (ctx.towerCo || 'Tower Co') + manager + (names.both ? 'them' : 'it') + ' now. The RTA has been read again.')
      }

      return out('The request was not saved: options have been proposed, so only a ' + (ctx.towerCo || 'Tower Co') + manager + 'it now. The RTA has been read again.')

    case 'not_allowed':
      // The API's 403 for a Tower Co user outside both responder gates (DEV-36, DEV-12). Worded here ONLY for the review
      // commands; for any other command the code has no copy of its own (the caller's fallback says the API's sentence).
      // The request save (P-S27: a Tower Co contact who is neither a manager nor the client) has its own. v2.2 (UX spec): it says what the account cannot
      // do, as the lock note does; it no longer names a manager (before the first proposal a Tower Co responder may edit).
      if (ctx.command === 'request') {
        return out('The request was not saved: your account can\'t change it. The RTA has been read again.')
      }

      if (ctx.command !== 'respond' && ctx.command !== 'submit') {
        return null
      }

      // falls through to the same sentence as not_client
    case 'not_client':
      // Decisions 76, 98: the client AND a Tower Co responder may record the response, so it no longer says "Only {Telco}".
      return out('Your account can\'t record this option\'s review, so the change wasn\'t saved. Its latest status is now shown.')

    case 'not_owner':
      return out('This option belongs to another client\'s RTA, so you can\'t change it.')

    case 'use_rta':
      return out('Candidate sites are now created through an RTA: Requests, Acquire.', 5500, false)

    // 428 version_required (XOL-595): this page sent no row version, so it is older than the server. The versioned-save notices (requestRowSync)
    // show their own dialog for it; this is the toast a command-less caller gets.
    case 'version_required':
    case 'use_close':
    case 'use_respond':
      return out('This page is out of date. Reload to see the RTA\'s current status.', 0, false)

    case 'not_transitioned':
      return out('Someone unlocked the approval, so the RTA is back at Step 4 and this wasn\'t saved. Finalize entries saved earlier are kept.', 0)

    case 'response_changed':
      // The option is the caller's (the summary's approved option), else the one the refusal names (`optionNo`: Complete and Submit decision read the
      // APPROVED option's unsubmitted change only, P-S1).
      var changedNo = present(ctx.changedOptionNo) && ctx.changedOptionNo !== 0 ? ctx.changedOptionNo : optionNoOf(ctx)

      // The Tower Co's own unsubmitted change (mirrors rtaDecision.actionView): nobody to wait for.
      if (ctx.changedByParty === 'towerco') {
        return out(option(changedNo).replace(/^./, function (c) { return c.toUpperCase() }) + '\'s review was changed and not submitted, so this can\'t go ahead yet. Submit or undo it in the option first.', 0)
      }

      return out(telco + ' changed its review of ' + option(changedNo) + ' and hasn\'t submitted it, so this can\'t go ahead yet. You can continue once ' + telco + ' submits.', 0)

    case 'stale_response':
      return out('Your review was changed elsewhere while you had it open. The latest review is now shown. Check it, then submit again.')

    case 'nothing_to_submit':
      return out('Nothing to submit: your review matches what you last submitted.')

    case 'reason_required':
      return out(reasonRequired(ctx.command), 5500, false)
  }

  return null
}

/* ------------------------------------------------------------------------
   Versioned field saves of the request and of an option (decision 159)
------------------------------------------------------------------------ */

// The codes that are LOCKS: they say nobody may write the thing now, whoever is behind. The server answers them before it looks for a conflict,
// so a refusal that carries a lock code is a lock even if its body also lists conflicts. 403 not_allowed (a Tower Co contact who is neither a manager
// nor the client, P-S27) is one too. decision_locked is the request's Finalize lock; an option answers Finalize with option_locked (P-S28).
var LOCK_CODES = ['request_locked', 'option_locked', 'decision_locked', 'not_allowed']

/**
 * What KIND of refusal a versioned field save (`PUT /actions/{id}` for a request field, `PUT /candidate/{id}` for an option column) got, read in the
 * order the server decides (P-S27): 'network' (no answer) | 'version_required' (428) | 'invalid' (422 read_only_fields, invalid_value, and the
 * duplicate Site Name / Site ID that follow the conflict check) | 'closed' (409 request_closed) | 'locked' (409 decision_locked, 403 not_allowed, 409
 * request_locked, 409 option_locked) | 'stale' (409 stale_field) | 'other'.
 * A LOCK wins over a conflict: a body with a lock code and `conflicts` is 'locked'. A 409 with no code that lists conflicts is 'stale'.
 */
export function saveRefusal (err) {
  var f = parseFailure(err)

  if (f.status === null) {
    return 'network'
  }

  if (f.status === 428 || f.code === 'version_required') {
    return 'version_required'
  }

  if (f.status === 422 || f.code === 'read_only_fields' || f.code === 'invalid_value') {
    return 'invalid'
  }

  if (f.code === 'request_closed') {
    return 'closed'
  }

  if (LOCK_CODES.indexOf(f.code) !== -1) {
    return 'locked'
  }

  if (f.code === 'stale_field' || (f.status === 409 && f.code === null && staleConflicts(f.data).length > 0)) {
    return 'stale'
  }

  return 'other'
}

/**
 * The conflicts of a 409 `stale_field` (D159): `{ conflicts: [{ path, current, changedAtVersion, by, org, at }] }` as
 * [{ path, field, current, changedAtVersion, by, org, at }], `field` being the request field a path names (null for an option column). A body
 * without conflicts is []. Never throws.
 */
export function staleConflicts (data) {
  var list = data && typeof data === 'object' && Array.isArray(data.conflicts) ? data.conflicts : []

  return list.filter(function (c) { return !!c && typeof c.path === 'string' }).map(function (c) {
    return {
      path: c.path,
      // Only a path under `state.nominal.` is a request field: a bare name ('district', 'state') is an option COLUMN of the same name.
      field: c.path.indexOf(NOMINAL_PATH_PREFIX) === 0 ? nominalFieldOf(c.path) : null,
      current: c.current,
      changedAtVersion: c.changedAtVersion,
      by: c.by || null,
      org: c.org || null,
      at: c.at || null
    }
  })
}

// The label of a conflicted path: a request field (`state.nominal.<field>`) or an option column, from requestSyncCopy.fieldLabel ("Other details" for a
// path it does not know, never a raw path).
function conflictLabel (c) {
  return c.field ? fieldLabel('action', 'state.nominal.' + c.field) : fieldLabel('option', c.path)
}

/**
 * The card of a 409 stale_field, in the strings the XOL-596 notices already use (`requestSync.stale.*`): { title, lead, footer, fields, count },
 * `fields` being [{ path, label, current, by, org, at, changedAtVersion }] (the SERVER's value is `current`: the card adopts it, and the caller shows the
 * user's own value from its stash). The lead names who changed it (`requestSync.stale.lead.named`), says "you, in another tab" when the same person
 * did (`.self`, `ctx.userName`), and falls back to "someone else" (`.unknown`). Null for a body with no conflicts.
 *
 * @param {Object} data the 409 body
 * @param {Object} [ctx] { userName }
 */
export function staleNotice (data, ctx) {
  var conflicts = staleConflicts(data)

  if (conflicts.length === 0) {
    return null
  }

  var me = ctx && ctx.userName ? ctx.userName : null
  var by = conflicts[0].by
  var same = conflicts.every(function (c) { return c.by === by })
  var n = conflicts.length
  var lead

  if (same && by && by === me) {
    lead = copyText('requestSync.stale.lead.self', { n: n })
  } else if (same && by) {
    lead = copyText('requestSync.stale.lead.named', { by: by, org: conflicts[0].org, n: n })
  } else {
    lead = copyText('requestSync.stale.lead.unknown', { n: n })
  }

  return {
    title: copyText('requestSync.stale.title', { n: n }),
    lead: lead,
    footer: copyText('requestSync.stale.footer'),
    count: n,
    fields: conflicts.map(function (c) {
      return { path: c.path, label: conflictLabel(c), current: c.current, by: c.by, org: c.org, at: c.at, changedAtVersion: c.changedAtVersion }
    })
  }
}

/**
 * A failed command in words: refusal(code) for a code this table knows; the API's own sentence for one it
 * does not (so a new code is never a blank toast); the connection sentence for a network error; and a plain
 * "Not saved" line for anything else. Always a toast: { title, message, duration, type, reread, code, status }.
 */
export function failureNotice (err, ctx) {
  var f = parseFailure(err)

  if (f.status === null) {
    return { code: null, status: null, title: 'Connection problem', message: MSG_NETWORK, duration: 5500, type: 'error', reread: false }
  }

  // The refusal's extra keys (DEV-59) go with the context, so a code's copy can name what the server said.
  var withExtras = {}

  Object.keys(ctx || {}).forEach(function (k) { withExtras[k] = ctx[k] })
  withExtras.extras = f.data

  // A conflict of a versioned field save (D159): the XOL-596 card. A lock code never gets here (saveRefusal reads the locks first).
  if (saveRefusal(err) === 'stale') {
    var stale = staleNotice(f.data, ctx)

    if (stale) {
      return { code: 'stale_field', status: f.status, title: stale.title, message: stale.lead + ' ' + stale.footer, duration: 0, type: 'warning', reread: false, stale: stale }
    }
  }

  var known = f.code ? refusal(f.code, withExtras) : null

  if (known) {
    known.status = f.status

    return known
  }

  var message = f.data && (f.data.message || f.data.error) ? String(f.data.message || f.data.error) : 'The change was not saved.'

  return { code: f.code, status: f.status, title: TITLE, message: message, duration: 5500, type: 'warning', reread: f.status === 409 }
}
